/**
 * IMPORTS
 */

import { useEffect, useMemo } from "react";

import { useQuery } from "@tanstack/react-query";

import { useStoreZustandUserAuth } from "@/store-zustand/user-auth";
import { useStoreZustandUsuarioCliente } from "@/store-zustand/usuarios-do-clientes";

import { USER_QUERY_STALE_TIME } from "@/common/const/query-cache";
import { handleGetUsuariosCliente } from "@/domain/use-cases/cutomers";

export interface IPagination {
  current_page: number;
  last_page: number;
  per_page: number;
  total: number;
}

type IUsuarioCliente = {
  id: number;
  user_id: number;
  nome: string;
  email: string;
  ativo: boolean | number;
  created_at: string;
  updated_at: string;
};

export interface IUsuariosClienteResponse {
  code: number;
  status: string;
  message: string;
  data: IUsuarioCliente[];
  pagination: IPagination;
}

function isUsuarioClienteAtivo(ativo: boolean | number | undefined): boolean {
  return ativo === true || ativo === 1;
}

function mapUsuarioClientesToTable(data: IUsuarioCliente[]): Record<string, any>[] {
  return data?.map((user) => ({
    id: user.id,
    nome: user?.nome || "N/A",
    email: user?.email || "N/A",
    status: isUsuarioClienteAtivo(user?.ativo) ? "ativo" : "inativo",
    data_criacao: user.created_at ? new Date(user.created_at).toLocaleString("pt-BR") : "N/A",
  }));
}

export function generatePagination(
  currentPage: number,
  lastPage: number,
  delta = 2
): (number | string)[] {
  const range: (number | string)[] = [];
  const left = Math.max(2, currentPage - delta);
  const right = Math.min(lastPage - 1, currentPage + delta);

  range.push(1);

  if (left > 2) {
    range.push("...");
  }

  for (let i = left; i <= right; i++) {
    range.push(i);
  }

  if (right < lastPage - 1) {
    range.push("...");
  }

  if (lastPage > 1) {
    range.push(lastPage);
  }

  return range;
}

function useUsuariosCliente(user_cliente_id: number) {
  const { currentPage, itemsPerPage, searchItem, setPaginations, setLastPage, setTotalItemsPage } =
    useStoreZustandUsuarioCliente();

  const { token: accessToken } = useStoreZustandUserAuth();

  // Chave de cache única por cliente, página atual, quantidade de itens por página e busca
  const queryKey = ["usuario-clientes", user_cliente_id, currentPage, itemsPerPage, searchItem];

  const {
    isLoading,
    error: isError,
    data: usuariosClienteResponse,
  } = useQuery<IUsuariosClienteResponse>({
    queryKey,
    queryFn: async () => {
      const response = await handleGetUsuariosCliente(
        accessToken!,
        user_cliente_id,
        currentPage,
        itemsPerPage,
        searchItem!
      );

      return response;
    },
    enabled: Boolean(user_cliente_id),
    staleTime: USER_QUERY_STALE_TIME,
  });

  const usuariosCliente = mapUsuarioClientesToTable(usuariosClienteResponse?.data as any);
  const pagination = usuariosClienteResponse?.pagination;

  // ✅ só recalcula se currentPage ou last_page mudar
  const pages = useMemo(() => {
    return generatePagination(currentPage, pagination?.last_page ?? 1);
  }, [currentPage, pagination?.last_page]);

  // ✅ atualiza Zustand uma única vez
  useEffect(() => {
    setPaginations(pages as number[]);
    setLastPage(pagination?.last_page as number);
    setTotalItemsPage(Number(pagination?.total));
  }, [pages, setPaginations, setLastPage, pagination, setTotalItemsPage]);

  return {
    usuariosCliente: usuariosCliente ?? [],
    paginations: pages,
    isLoading,
    isError,
  };
}

/**
 * EXPORTS
 */
export { useUsuariosCliente };
